import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassInput } from '@/components/ui/GlassInput'
import { GlassButton } from '@/components/ui/GlassButton'
import { api } from '@/lib/api'
import { opsSecretsQueryOptions } from '@/lib/queryOptions'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/settings/ops/secrets')({
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(opsSecretsQueryOptions())
  },
  component: OpsSecretsPage,
})

function OpsSecretsPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data: secrets = [] } = useQuery(opsSecretsQueryOptions())
  const [name, setName] = useState('')
  const [provider, setProvider] = useState('')
  const [value, setValue] = useState('')

  const createMutation = useMutation({
    mutationFn: api.ops.secrets.create,
    onSuccess: () => {
      setName('')
      setProvider('')
      setValue('')
      qc.invalidateQueries({ queryKey: ['ops', 'secrets'] })
    },
  })

  const disableMutation = useMutation({
    mutationFn: api.ops.secrets.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops', 'secrets'] }),
  })

  return (
    <RootLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-xl font-bold text-white">{t('settings.opsSecrets.title')}</h1>
          <p className="text-sm text-white/40 mt-0.5">{t('settings.opsSecrets.subtitle')}</p>
        </div>

        <GlassCard className="space-y-4">
          <h2 className="font-semibold text-white text-sm">{t('settings.opsSecrets.createTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput
              label={t('settings.opsSecrets.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.opsSecrets.namePlaceholder')}
            />
            <GlassInput
              label={t('settings.opsSecrets.providerLabel')}
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder={t('settings.opsSecrets.providerPlaceholder')}
            />
          </div>
          <GlassInput
            label={t('settings.opsSecrets.valueLabel')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('settings.opsSecrets.valuePlaceholder')}
            type="password"
          />
          <GlassButton
            onClick={() => createMutation.mutate({ name, provider, value })}
            loading={createMutation.isPending}
            disabled={!name || !provider || !value}
          >
            {t('settings.opsSecrets.createButton')}
          </GlassButton>
        </GlassCard>

        <div className="space-y-3">
          {secrets.map((secret) => (
            <GlassCard key={secret.id} size="sm" className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-white">{secret.name}</p>
                <p className="text-xs text-white/40">{secret.provider} · {secret.scope}</p>
              </div>
              <GlassButton
                size="sm"
                variant="ghost"
                onClick={() => disableMutation.mutate(secret.id)}
                loading={disableMutation.isPending}
              >
                {t('settings.opsSecrets.disableButton')}
              </GlassButton>
            </GlassCard>
          ))}
        </div>
      </div>
    </RootLayout>
  )
}
